import { ChevronDownIcon } from '@assets/icons';
import { Group } from '@components/layout';
import { Typography } from '@components/typography';
import { Box, Button, CircularProgress, Collapse, useTheme } from '@mui/material';
import { useState } from 'react';
import type { ContentBlock } from './agentChatTypes';

/**
 * Builds a short human-readable summary for the collapsed tool call header.
 * e.g. "Bash" → "Bash — ls -la", "Read" → "Read — /path/to/file.ts"
 */
const toolCallSummary = (name: string, input: Record<string, unknown>): string => {
  const preview =
    (input.command as string) ??
    (input.file_path as string) ??
    (input.pattern as string) ??
    (input.prompt as string) ??
    (input.query as string) ??
    (input.url as string) ??
    '';
  const maxLen = 80;
  return preview.length > maxLen ? `${preview.slice(0, maxLen)}…` : preview;
};

/**
 * Collapsible component for a single tool call (tool_use + its tool_result).
 * Collapsed by default — shows a one-line summary with a chevron arrow.
 * A small spinner replaces the chevron while the tool is still running.
 */
export const AgentChatToolBlock = ({
  block,
}: {
  block: Extract<ContentBlock, { type: 'tool_call' }>;
}) => {
  const { typography } = useTheme();
  const [open, setOpen] = useState(false);
  const summary = toolCallSummary(block.name, block.input);
  const isRunning = block.status === 'running';

  return (
    <Box data-testid="agent-chat-tool-block">
      <Button
        size="small"
        variant="text"
        onClick={() => setOpen((prev) => !prev)}
        sx={{
          color: 'agentChat.message.agent.foreground.light',
          fontSize: '0.75rem',
          p: 0.5,
          minWidth: 0,
          width: '100%',
          textTransform: 'none',
          fontFamily: 'monospace',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          gap: 0.5,
          '&:hover': {
            backgroundColor: 'agentChat.message.agent.backgroundHover',
          },
        }}
      >
        <Group alignItems="center" justifyContent="flex-start" gap={1.2}>
          {isRunning ? (
            <CircularProgress
              size={10}
              sx={{ color: 'agentChat.message.agent.foreground.light', flexShrink: 0 }}
            />
          ) : (
            <ChevronDownIcon
              size={14}
              style={{
                flexShrink: 0,
                transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s',
              }}
            />
          )}
          <Typography variant="caption1" color="agentChat.message.agent.foreground.default">
            {block.name}
          </Typography>
          {summary && (
            <>
              <span>-</span>
              <Typography
                variant="caption1"
                color="agentChat.message.agent.foreground.light"
                textAlign="left"
              >
                {summary}
              </Typography>
            </>
          )}
        </Group>
      </Button>
      <Collapse in={open}>
        <Box
          sx={{
            ml: 2,
            mt: 0.5,
            borderLeft: '2px solid',
            borderColor: 'agentChat.message.agent.foreground.light',
            pl: 1.5,
          }}
        >
          {/* Input parameters */}
          <Typography
            variant="caption1"
            sx={{
              color: 'agentChat.message.agent.foreground.default',
            }}
          >
            Input
          </Typography>
          <Box
            component="pre"
            sx={{
              bgcolor: 'background.default',
              borderRadius: 1,
              p: 1,
              ...typography.caption2,
              overflowX: 'auto',
              color: 'text.light',
              mt: 0.25,
              mb: 1,
              maxHeight: 300,
              overflowY: 'auto',
            }}
          >
            {JSON.stringify(block.input, null, 2)}
          </Box>

          {/* Result (only shown once the tool finishes) */}
          {block.result !== undefined && (
            <>
              <Typography
                variant="caption1"
                sx={{
                  color: 'agentChat.message.agent.foreground.default',
                }}
              >
                Result
              </Typography>
              <Box
                component="pre"
                sx={{
                  bgcolor: 'background.default',
                  borderRadius: 1,
                  p: 1,
                  ...typography.caption2,
                  overflowX: 'auto',
                  color: 'text.light',
                  mt: 0.25,
                  maxHeight: 300,
                  overflowY: 'auto',
                }}
              >
                {typeof block.result === 'string'
                  ? block.result
                  : JSON.stringify(block.result, null, 2)}
              </Box>
            </>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};
